/**
 * AASB 123 — Borrowing Costs. Rulepack v1.0.0.
 * Validates capitalisation of borrowing costs on qualifying assets.
 */
import { Finding, ValidationResult, DISCLAIMER } from './types';
import {
  CellValue, parseNumber, parseBool, parsePercent, parseDate, makeHeaderMap, normaliseHeader,
  acceptedHeadersFor, findingBuilder, severityCounts, compliantCount, fmt,
} from './_shared';

const VERSION = 'aasb123-1.0.0';
const fb = findingBuilder('AASB_123' as any, VERSION);

const HMAP = makeHeaderMap([
  ['asset_id', 'asset id', 'project id', 'id'],
  ['qualifying_asset', 'qualifying asset', 'qualifying flag'],
  ['borrowing_cost', 'borrowing cost', 'interest cost'],
  ['capitalisation_rate', 'capitalisation rate', 'cap rate'],
  ['expenditure', 'expenditure', 'qualifying expenditure'],
  ['capitalised_amount', 'capitalised', 'capitalised amount', 'capitalized amount'],
  ['expensed_amount', 'expensed', 'expensed amount'],
  ['capitalisation_start', 'capitalisation start', 'cap start'],
  ['capitalisation_end', 'capitalisation end', 'cap end'],
]);

const REQUIRED = ['asset_id', 'borrowing_cost'];

export function detectAASB123(headers: string[]) {
  const hits = new Set<string>();
  for (const r of headers) { const c = normaliseHeader(HMAP, String(r ?? '')); if (c) hits.add(c); }
  const matched = REQUIRED.filter(r => hits.has(r));
  const boost = ['borrowing cost', 'capitalisation', 'qualifying'].some(k => headers.some(h => String(h ?? '').toLowerCase().includes(k)));
  return { confidence: Math.min(1, (matched.length / REQUIRED.length) * (boost ? 1.5 : 1)), matched, missing: REQUIRED.filter(r => !hits.has(r)) };
}

export function validateAASB123(headers: string[], data: CellValue[][], _asAt: Date = new Date()): ValidationResult {
  const findings: Finding[] = [];
  const schemaErrors: string[] = [];
  const idx: Record<string, number> = {};
  headers.forEach((h, i) => { const c = normaliseHeader(HMAP, String(h ?? '')); if (c) idx[c] = i; });
  for (const f of REQUIRED) if (idx[f] === undefined) schemaErrors.push(`Missing column "${f}". Accepted: ${acceptedHeadersFor(HMAP, f).join(', ')}.`);
  if (schemaErrors.length) {
    return { summary: { standard: 'AASB_123' as any, rulepackVersion: VERSION, rowsTested: 0, compliant: 0, byseverity: { info: 0, warning: 0, error: 0 }, runAt: new Date().toISOString() }, findings, schemaErrors, disclaimer: DISCLAIMER };
  }

  const rowIds: string[] = [];
  data.forEach((raw, i) => {
    const rowNum = i + 2;
    const get = (k: string) => raw[idx[k]];
    const id = String(get('asset_id') ?? `row-${rowNum}`);
    rowIds.push(id);

    const missing = REQUIRED.filter(f => { const v = get(f); return v == null || v === ''; });
    if (missing.length) { findings.push(fb.build('AASB123.R01', 'error', 'Required field missing', `Row ${rowNum} (${id}): missing ${missing.join(', ')}.`, { rowId: id })); return; }

    const cost = parseNumber(get('borrowing_cost'))!;
    const qualifying = idx.qualifying_asset !== undefined ? parseBool(get('qualifying_asset')) : null;
    const capRate = idx.capitalisation_rate !== undefined ? parsePercent(get('capitalisation_rate')) : null;
    const expenditure = idx.expenditure !== undefined ? parseNumber(get('expenditure')) : null;
    const capitalised = idx.capitalised_amount !== undefined ? parseNumber(get('capitalised_amount')) : null;
    const expensed = idx.expensed_amount !== undefined ? parseNumber(get('expensed_amount')) : null;
    const capStart = idx.capitalisation_start !== undefined ? parseDate(get('capitalisation_start')) : null;
    const capEnd = idx.capitalisation_end !== undefined ? parseDate(get('capitalisation_end')) : null;

    // R02 non-qualifying → all expensed
    if (qualifying === false) {
      if (capitalised !== null && capitalised > 0) {
        findings.push(fb.build('AASB123.R02', 'error', 'Non-qualifying asset — borrowing cost should be expensed',
          `Asset ${id}: qualifying_asset=false but capitalised $${fmt(capitalised)}. AASB 123.8 — only directly attributable borrowing costs on qualifying assets are capitalised; others are expensed.`,
          { rowId: id }));
      }
    }

    // R03 capitalised + expensed = total cost
    if (capitalised !== null && expensed !== null) {
      const sum = capitalised + expensed;
      const tol = Math.max(20, 0.005 * Math.abs(cost || 1));
      if (Math.abs(sum - cost) > tol) {
        findings.push(fb.build('AASB123.R03', 'error', 'Capitalised + Expensed ≠ Total borrowing cost',
          `Asset ${id}: capitalised $${fmt(capitalised)} + expensed $${fmt(expensed)} = $${fmt(sum)}, but total cost $${fmt(cost)}.`,
          { rowId: id }));
      }
    }

    // R04 capitalised amount sanity (≤ rate × expenditure)
    if (qualifying === true && capRate !== null && expenditure !== null && capitalised !== null) {
      const cap = capRate * expenditure;
      if (capitalised > cap * 1.05) {
        findings.push(fb.build('AASB123.R04', 'warning', 'Capitalised amount may exceed allowable',
          `Asset ${id}: capitalised $${fmt(capitalised)} vs (cap rate ${(capRate * 100).toFixed(2)}% × expenditure $${fmt(expenditure)} = $${fmt(cap)}). AASB 123.14 caps capitalisation at actual borrowing costs incurred during the period.`,
          { rowId: id }));
      }
    }

    // R05 capitalisation period sanity
    if (capStart && capEnd && capEnd < capStart) {
      findings.push(fb.build('AASB123.R05', 'error', 'Capitalisation end before start',
        `Asset ${id}: cap_start ${capStart.toISOString().slice(0, 10)}, cap_end ${capEnd.toISOString().slice(0, 10)}.`,
        { rowId: id }));
    }
  });

  return {
    summary: { standard: 'AASB_123' as any, rulepackVersion: VERSION, rowsTested: rowIds.length, compliant: compliantCount(rowIds, findings), byseverity: severityCounts(findings), runAt: new Date().toISOString() },
    findings, disclaimer: DISCLAIMER,
  };
}
